import { useEffect, useState } from "react";

import { VIEW_QUADRANTS, VIEW_TRIPLE } from "../../utils/dewarping";

/**
 * Use this hook alongside VideoPlayer component when supporting at least one of zooming or dewarping. Allows zooming and panning to be done through keyboard presses.
 * @param {object} focusableContainerRef Ref applied to a parent element. When this element is in focus, the user will be able to zoom and pan using keyboard presses.
 * @param {object} config
 * @param {boolean} [config.disabled] Disable keyboard controls.
 * @param {('single', 'triple', 'quadrants')} [config.dewarpView] Set if showing a dewarped view. Otherwise it is assumed normal zoom is being used.
 * @param {object} [config.zoomControlsRef] Ref applied to ZoomableWrapper.
 * @param {object} [config.playerRef] Ref applied to VideoPlayer through which we access the dewarp control functions.
 */
const useKeyboardZoomControls = (focusableContainerRef, { disabled = false, dewarpView, zoomControlsRef, playerRef } = {}) => {
    
    const [state, setState] = useState({
        // User presses Enter to activate these controls
        active: false,
        // Selected camera view 0-3 for dewarping
        selectedView: null
    });

    // Reset state if controls are disabled
    useEffect(() => {
        if (disabled) {
            setState({
                active: false,
                selectedView: null
            });
        }
    }, [disabled]);
    
    // Set up listeners
    useEffect(() => {
        if (!disabled && focusableContainerRef) {
            
            const focusableContainer = focusableContainerRef.current;

            let keyHandler;

            if (state.active) {
                if (dewarpView) {
                    keyHandler = (event) => {
                        switch (event.key) {
                            case '+':
                                playerRef.current.dewarpZoom(state.selectedView, -5);
                                event.preventDefault();
                                return;
                            case '-':
                                playerRef.current.dewarpZoom(state.selectedView, 5);
                                event.preventDefault();
                                return;
                            case 'ArrowRight':
                                playerRef.current.dewarpMoveX(state.selectedView, 5);
                                event.preventDefault();
                                return;
                            case 'ArrowLeft':
                                playerRef.current.dewarpMoveX(state.selectedView, -5);
                                event.preventDefault();
                                return;
                            case 'ArrowUp':
                                playerRef.current.dewarpMoveY(state.selectedView, 5);
                                event.preventDefault();
                                return;
                            case 'ArrowDown':
                                playerRef.current.dewarpMoveY(state.selectedView, -5);
                                event.preventDefault();
                                return;
                            case 'Escape':
                                setState({
                                    active: false,
                                    selectedView: null
                                });
                                event.preventDefault();
                                event.stopPropagation();
                                return;
                            case 'Tab':
                                // Tab through different dewarp cameras
                                const maxViewIndex = dewarpView === VIEW_QUADRANTS ? 3 : dewarpView === VIEW_TRIPLE ? 2 : 0;
                                let newIndex;
                                if (event.shiftKey) {
                                    newIndex = state.selectedView - 1;
                                    if (newIndex === -1) {
                                        newIndex = maxViewIndex;
                                    }
                                } else {
                                    newIndex = state.selectedView + 1;
                                    if (newIndex > maxViewIndex) {
                                        newIndex = 0;
                                    }
                                }
                                setState({
                                    active: true,
                                    selectedView: newIndex
                                });
                                event.preventDefault();
                                return;
                            default:
                                return;
                        }
                    }
                } else {
                    // Normal zooming/panning
                    keyHandler = (event) => {
                        const ARROW_STEP_SIZE = 40;
                        const transformState = zoomControlsRef.current.instance.transformState;
                        const bounds = zoomControlsRef.current.instance.bounds;

                        switch (event.key) {
                            case '+':
                                zoomControlsRef.current.zoomIn();
                                event.preventDefault();
                                return;
                            case '-':
                                zoomControlsRef.current.zoomOut();
                                event.preventDefault();
                                return;
                            case 'ArrowRight':
                                zoomControlsRef.current.setTransform(Math.max(transformState.positionX - ARROW_STEP_SIZE, bounds.minPositionX), transformState.positionY, transformState.scale);
                                event.preventDefault();
                                return;
                            case 'ArrowLeft':
                                zoomControlsRef.current.setTransform(Math.min(transformState.positionX + ARROW_STEP_SIZE, bounds.maxPositionX), transformState.positionY, transformState.scale);
                                event.preventDefault();
                                return;
                            case 'ArrowUp':
                                zoomControlsRef.current.setTransform(transformState.positionX, Math.min(transformState.positionY + ARROW_STEP_SIZE, bounds.maxPositionY), transformState.scale);
                                event.preventDefault();
                                return;
                            case 'ArrowDown':
                                zoomControlsRef.current.setTransform(transformState.positionX, Math.max(transformState.positionY - ARROW_STEP_SIZE, bounds.minPositionY), transformState.scale);
                                event.preventDefault();
                                return;
                            case 'Escape':
                                setState({
                                    active: false,
                                    selectedView: null
                                });
                                event.stopPropagation();
                                event.preventDefault();
                                return;
                            default:
                                return;
                        }
                    }
                }
            } else {
                // Wait until Enter pressed to become active
                keyHandler = (event) => {
                    if (event.key === 'Enter') {
                        setState({
                            active: true,
                            selectedView: 0
                        });
                    }
                }
            }
            
            focusableContainer.addEventListener('keydown', keyHandler);

            return () => {
                focusableContainer.removeEventListener('keydown', keyHandler);
            };
        }
    }, [disabled, focusableContainerRef, zoomControlsRef, dewarpView, playerRef, state.active, state.selectedView]);

    return state;
}

export default useKeyboardZoomControls;